import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle
} from '@headlessui/react'
import { Printer, X } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import './print-styles.css'
import printStyleContent from './print-styles.css?inline'
import {
  useMasterBillActions,
  useMasterBillState
} from './stores/useMasterBillStore'
import { useSearchState } from './stores/useSearchStore'

import { HouseBillSchema, MasterBillSchema } from '@/services/CustomsV2Service'

interface CargoTemplateData {
  masterBill: MasterBillSchema
  houseBills: HouseBillSchema[]
}

const PrintPreview: React.FC = () => {
  const printRef = useRef<HTMLDivElement>(null)
  const [data, setData] = useState<CargoTemplateData | null>(null)
  const { masterBillId, masterBills } = useSearchState()
  const { masterBill } = useMasterBillState()
  const { closePrintDialog } = useMasterBillActions()

  const loadPrintData = useCallback(async () => {
    if (!masterBillId || masterBill === null) {
      return
    }

    const houseBills = masterBills.find(
      (bill) => bill.id === masterBillId
    )?.houseBills
    setData({
      masterBill: masterBill,
      houseBills: houseBills ?? []
    })
  }, [masterBillId, masterBill])

  useEffect(() => {
    loadPrintData()
  }, [loadPrintData])

  const handlePrint = () => {
    if (printRef.current) {
      // 获取当前页面的HTML内容
      const htmlContent = `
            <!DOCTYPE html>
            <html>
              <head>
                <title>拆货表 - ${data?.masterBill.masterBlNumber}</title>
                <style>${printStyleContent}</style>
              </head>
              <body>
                ${printRef.current.innerHTML}
              </body>
            </html>
          `
      const printWindow = window.open('', '_blank')
      if (printWindow) {
        printWindow.document.documentElement.innerHTML = htmlContent
        printWindow.document.close()
        printWindow.focus()
        setTimeout(() => {
          printWindow.print()
          printWindow.close()
        }, 250)
      }
    }
  }

  const getBooleanDisplay = (value?: boolean) => {
    return value ? '⚫︎' : ''
  }

  const { printDialog } = useMasterBillState()

  return (
    <Dialog
      open={printDialog}
      onClose={closePrintDialog}
      className="relative z-50"
    >
      <DialogBackdrop className="fixed inset-0 bg-black/50 transition-opacity duration-300 ease-out data-[closed]:opacity-0" />

      <div className="fixed inset-0 flex items-center justify-center p-2">
        <DialogPanel className="w-full h-full max-w-[98vw] max-h-[98vh] transform overflow-hidden rounded-lg bg-white shadow-xl transition-all duration-300 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 flex flex-col">
          {data && (
            <>
              {/* 工具栏 */}
              <div className="flex justify-between items-center p-4 border-b border-gray-200 no-print bg-gray-50 flex-shrink-0">
                <DialogTitle className="text-xl font-semibold text-gray-900">
                  拆货表打印预览
                </DialogTitle>
                <div className="flex space-x-3">
                  <button
                    onClick={handlePrint}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    打印
                  </button>
                  <button
                    onClick={closePrintDialog}
                    className="inline-flex items-center px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                  >
                    <X className="w-4 h-4 mr-2" />
                    关闭
                  </button>
                </div>
              </div>

              {/* 打印内容 */}
              <div className="flex-1 overflow-auto">
                <div ref={printRef} className="cargo-print-container p-6">
                  {/* 表格 */}
                  <table className="cargo-table">
                    <tbody>
                      {/* 第一行：デバン詳細表标题 */}
                      <tr>
                        <td colSpan={12} className="title-header">
                          デバン詳細表
                        </td>
                      </tr>

                      {/* 第二行：主提单信息 */}
                      <tr>
                        <td className="info-label">MB/L詳細</td>
                        <td colSpan={5} className="info-content">
                          {`${data.masterBill.customerNumber}-${
                            data.masterBill.masterBlNumber
                          }-${data.masterBill.containerNumber || ''}-${
                            data.masterBill.totalPackages
                          }${data.masterBill.totalUnit}-${
                            data.masterBill.containerSize
                          }${data.masterBill.containerType}`}
                        </td>
                        <td className="info-label">デバン日</td>
                        <td className="info-date">{`${data.masterBill.devanDate ?? ""}`}</td>
                        <td className="info-label">搬入予定日</td>
                        <td className="info-date">{`${data.masterBill.inboundDate ?? ""}`}</td>
                        <td className="info-label">Freetime</td>
                        <td className="info-date">{`${data.masterBill.freetimeDate ?? ""}`}</td>
                      </tr>

                      {/* 分提单分组数据 */}
                      {(() => {
                        return data.houseBills.map((houseBill, hblIndex) => {
                          // const houseBillNumber = hblIndex + 1
                          const cargoDetails = houseBill.cargoDetails || []
                          const totalRows = cargoDetails.length + 1 // 列标题行 + 分货行数

                          return (
                            <React.Fragment key={hblIndex}>
                              {/* 分提单标题行 */}
                              <tr
                                className={
                                  hblIndex % 2 === 0
                                    ? 'hbl-even-row'
                                    : 'hbl-odd-row'
                                }
                              >
                                <td
                                  rowSpan={totalRows + 1}
                                  className="house-number"
                                >
                                  {houseBill.sequenceNumber}
                                </td>
                                <td className="info-label">HB/L</td>
                                <td colSpan={2} className="hbl-number">
                                  {houseBill.houseBlNumber}
                                </td>
                                <td className="info-label" rowSpan={2}>
                                  数量
                                </td>
                                <td colSpan={7} className="packages-info">
                                  {houseBill.packages || 0}
                                </td>
                              </tr>

                              {/* 列标题行 */}
                              <tr
                                className={
                                  hblIndex % 2 === 0
                                    ? 'hbl-even-row'
                                    : 'hbl-odd-row'
                                }
                              >
                                <td className="col-header">番号</td>
                                <td colSpan={2} className="col-header">
                                  Marks
                                </td>
                                {/* <td className="col-header" >数量</td> */}
                                <td className="col-header">デバン数量確認</td>
                                <td className="col-header">NACCS許可</td>
                                <td className="col-header">輸入許可</td>
                                <td className="col-header">出荷予定日</td>
                                <td className="col-header">出荷完了日</td>
                                <td className="col-header">検査日</td>
                                <td className="col-header">備考欄</td>
                              </tr>

                              {/* 分货号数据行 */}
                              {cargoDetails.length > 0 ? (
                                cargoDetails.map((cargo, cargoIndex) => {
                                  const marks = cargo.cargoNumber || '' // 直接使用cargoNumber字段

                                  return (
                                    <tr
                                      key={`${hblIndex}-${cargoIndex}`}
                                      className={
                                        hblIndex % 2 === 0
                                          ? 'hbl-even-row'
                                          : 'hbl-odd-row'
                                      }
                                    >
                                      <td className="cargo-number">
                                        {cargo.sequenceNumber}
                                      </td>
                                      <td colSpan={2} className="marks">
                                        {marks}
                                      </td>
                                      <td className="packages">
                                        {cargo.packages || 0}
                                      </td>
                                      <td className="confirm-qty"></td>
                                      {/* NACCS许可、输入许可、出荷予定日、出荷完了日、检査日、备考栏字段只在第一行显示，使用rowSpan合并 */}
                                      {cargoIndex === 0 && (
                                        <>
                                          <td
                                            className="permit-status"
                                            rowSpan={cargoDetails.length}
                                          >
                                            {getBooleanDisplay(
                                              houseBill.naccsPermitStatus
                                            )}
                                          </td>
                                          <td
                                            className="permit-status"
                                            rowSpan={cargoDetails.length}
                                          >
                                            {getBooleanDisplay(
                                              houseBill.importPermitStatus
                                            )}
                                          </td>
                                          <td
                                            className="date-field"
                                            rowSpan={cargoDetails.length}
                                          >
                                            {/* 出荷予定日 - 暂时为空 */}
                                          </td>
                                          <td
                                            className="date-field"
                                            rowSpan={cargoDetails.length}
                                          >
                                            {/* 出荷完了日 - 暂时为空 */}
                                          </td>
                                          <td
                                            className="date-field"
                                            rowSpan={cargoDetails.length}
                                          >
                                            {/* 检査日 - 暂时为空 */}
                                          </td>
                                          <td
                                            className="remarks"
                                            rowSpan={cargoDetails.length}
                                          >
                                            {/* 備考欄 - 暂时为空 */}
                                          </td>
                                        </>
                                      )}
                                    </tr>
                                  )
                                })
                              ) : (
                                // 如果没有分货详情，显示一个空行但包含许可状态和其他合并字段
                                <tr
                                  className={
                                    hblIndex % 2 === 0
                                      ? 'hbl-even-row'
                                      : 'hbl-odd-row'
                                  }
                                >
                                  <td className="cargo-number">-</td>
                                  <td colSpan={2} className="marks">
                                    -
                                  </td>
                                  <td className="packages">-</td>
                                  <td className="confirm-qty"></td>
                                  <td className="permit-status">
                                    {getBooleanDisplay(
                                      houseBill.naccsPermitStatus
                                    )}
                                  </td>
                                  <td className="permit-status">
                                    {getBooleanDisplay(
                                      houseBill.importPermitStatus
                                    )}
                                  </td>
                                  <td className="date-field">
                                    {/* 出荷予定日 - 暂时为空 */}
                                  </td>
                                  <td className="date-field">
                                    {/* 出荷完了日 - 暂时为空 */}
                                  </td>
                                  <td className="date-field">
                                    {/* 检査日 - 暂时为空 */}
                                  </td>
                                  <td className="remarks">
                                    {/* 備考欄 - 暂时为空 */}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          )
                        })
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  )
}

export default PrintPreview
